import { useCallback, useEffect, useRef, useState, type PointerEvent } from "react";
import { Link } from "react-router";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { IconBack, IconNext } from "./icons";

// The home page slideshow (managed in Admin → Banners). A slide is a picture per language, or a text slide in
// one of three house styles, with an optional button that links inside the store.

type Pair = { ar: string; en: string };
type Img = { wide: string; small: string } | null;
export type Slide = {
  id: string;
  title: Pair;
  subtitle: Pair;
  cta: Pair;
  link: string;
  theme: "ink" | "green" | "light";
  /** Off when the picture already carries its own words. */
  showText: boolean;
  image: { ar: Img; en: Img };
  /** Built-in slides only: a product photo shown beside the text. */
  photo?: string | null;
};

const AUTOPLAY_MS = 6000;
const reducedMotion = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** One slide; also used as the live preview in the admin editor. */
export function BannerSlide({ slide, locale, active = true }: { slide: Slide; locale: "ar" | "en"; active?: boolean }) {
  const pick = (p: Pair) => p[locale] || p[locale === "ar" ? "en" : "ar"];
  const img = slide.image[locale] ?? slide.image[locale === "ar" ? "en" : "ar"];
  const title = pick(slide.title);
  const subtitle = pick(slide.subtitle);
  const cta = pick(slide.cta);
  const body = (
    <>
      {img && (
        <picture className="banner-img">
          <source media="(max-width: 640px)" srcSet={img.small} />
          <img src={img.wide} alt="" loading={active ? "eager" : "lazy"} />
        </picture>
      )}
      {(slide.showText || !img) && (title || subtitle || cta) && (
        <div className="banner-text">
          {title && <h2 dir="auto">{title}</h2>}
          {subtitle && <p dir="auto">{subtitle}</p>}
          {cta && <span className="banner-cta">{cta}</span>}
        </div>
      )}
      {!img && slide.photo && <img className="banner-photo" src={slide.photo} alt="" loading="lazy" />}
    </>
  );
  const cls = `banner theme-${slide.theme}${img ? " has-img" : ""}${slide.photo && !img ? " has-photo" : ""}`;
  // The whole slide is the link, so it's one tap target, named by its title even when the words are in the
  // picture; slides out of view leave the tab order.
  return (
    <Link to={slide.link} className={cls} tabIndex={active ? 0 : -1} aria-hidden={!active} aria-label={[title, subtitle].filter(Boolean).join(". ") || cta}>
      {body}
    </Link>
  );
}

/** The slideshow at the top of the home page. Moves on by itself, pauses while you look, swipes on phones. */
export function HomeBanners() {
  const { t, locale } = useI18n();
  const [slides, setSlides] = useState<Slide[]>([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(reducedMotion());
  const start = useRef<number | null>(null);
  const swiped = useRef(false);

  useEffect(() => {
    api<{ slides: Slide[] }>("/api/banners").then((r) => setSlides(r.slides)).catch(() => setSlides([]));
  }, []);

  const count = slides.length;
  const go = useCallback((i: number) => setIndex(((i % count) + count) % count), [count]);

  useEffect(() => {
    if (paused || count < 2) return;
    const id = setTimeout(() => go(index + 1), AUTOPLAY_MS);
    return () => clearTimeout(id);
  }, [index, paused, count, go]);

  useEffect(() => {
    const vis = () => setPaused((p) => (document.hidden ? true : reducedMotion() ? p : false));
    document.addEventListener("visibilitychange", vis);
    return () => document.removeEventListener("visibilitychange", vis);
  }, []);

  if (!count) return null;
  const rtl = locale === "ar";
  // In Arabic the next slide comes in from the left, as reading goes right to left.
  const offset = (rtl ? 1 : -1) * index * 100;
  const onDown = (e: PointerEvent) => {
    start.current = e.clientX;
    swiped.current = false;
  };
  const onUp = (e: PointerEvent) => {
    if (start.current === null) return;
    const dx = e.clientX - start.current;
    start.current = null;
    if (Math.abs(dx) < 40) return;
    swiped.current = true; // a swipe isn't a tap: don't follow the slide's link
    go(index + ((dx < 0) !== rtl ? 1 : -1));
  };

  return (
    <section
      className="banners"
      aria-roledescription="carousel"
      aria-label={t("banners.label")}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(reducedMotion())}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(reducedMotion())}
    >
      <div
        className="banners-viewport"
        onPointerDown={onDown}
        onPointerUp={onUp}
        onClickCapture={(e) => { if (swiped.current) { e.preventDefault(); swiped.current = false; } }}
        onDragStart={(e) => e.preventDefault()}
      >
        <div className="banners-track" style={{ transform: `translateX(${offset}%)` }}>
          {slides.map((s, i) => (
            <div key={s.id} className="banners-slide" role="group" aria-roledescription="slide" aria-label={t("banners.slide", { n: i + 1, total: count })}>
              <BannerSlide slide={s} locale={locale} active={i === index} />
            </div>
          ))}
        </div>
      </div>
      {count > 1 && (
        <div className="banners-nav">
          <button type="button" className="banners-arrow" onClick={() => go(index - 1)} aria-label={t("banners.prev")}><IconBack size={18} /></button>
          <div className="banners-dots">
            {slides.map((s, i) => (
              <button key={s.id} type="button" className={i === index ? "on" : ""} onClick={() => go(i)} aria-label={t("banners.slide", { n: i + 1, total: count })} aria-current={i === index} />
            ))}
          </div>
          <button type="button" className="banners-arrow" onClick={() => go(index + 1)} aria-label={t("banners.next")}><IconNext size={18} /></button>
        </div>
      )}
    </section>
  );
}
